import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { 
  ArrowRight, CheckCircle, Database, Smartphone, Globe, 
  Server, Code, Cloud, Sparkles, ArrowLeft, Zap,
  Settings, Shield, Clock, Users, TrendingUp, Layers
} from 'lucide-react';

const serviceData = {
  sap: {
    title: 'SAP Services',
    subtitle: 'Enterprise Resource Planning Excellence',
    description: 'Transform your business operations with our comprehensive SAP solutions. From implementation to migration, we deliver world-class SAP services.',
    icon: Database,
    color: 'from-blue-500 to-indigo-600',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    services: [
      { name: 'SAP S/4 HANA Migration', desc: 'Seamless migration to the latest SAP platform' },
      { name: 'SAP Implementation', desc: 'End-to-end SAP deployment for your organization' },
      { name: 'SAP Upgradation', desc: 'Keep your SAP systems current and optimized' },
      { name: 'SAP Consulting', desc: 'Expert guidance for your SAP strategy' },
      { name: 'SAP Support Services', desc: '24/7 technical support and maintenance' },
      { name: 'SAP Integration', desc: 'Connect SAP with your existing systems' }
    ],
    features: [
      { icon: Shield, title: 'Certified Experts', desc: 'SAP certified consultants with 10+ years experience' },
      { icon: Clock, title: 'On-Time Delivery', desc: 'Proven track record of meeting project deadlines' },
      { icon: TrendingUp, title: 'ROI Focused', desc: 'Maximize your return on SAP investment' }
    ],
    technologies: ['ABAP', 'FICO', 'MM', 'SD', 'PP/QM', 'EWM', 'BASIS', 'CPI/DS', 'PI/PO', 'UI5', 'S/4 HANA']
  },
  mobile: {
    title: 'Mobile Development',
    subtitle: 'Native & Cross-Platform Apps',
    description: 'Build powerful mobile experiences that engage users and drive business growth. Our mobile solutions work seamlessly across all devices.',
    icon: Smartphone,
    color: 'from-green-500 to-emerald-600',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80',
    services: [
      { name: 'iOS App Development', desc: 'Native Swift/SwiftUI applications for Apple devices' },
      { name: 'Android Development', desc: 'Kotlin/Java apps for the Android ecosystem' },
      { name: 'Cross-Platform Apps', desc: 'React Native & Flutter for multi-platform reach' },
      { name: 'Enterprise Mobile Apps', desc: 'Secure business applications for workforce' },
      { name: 'Mobile App Redesign', desc: 'Modernize your existing mobile applications' },
      { name: 'App Maintenance', desc: 'Ongoing support and updates for your apps' }
    ],
    features: [
      { icon: Layers, title: 'Multi-Platform', desc: 'Single codebase, multiple platforms' },
      { icon: Zap, title: 'High Performance', desc: 'Optimized for speed and battery life' },
      { icon: Shield, title: 'Secure by Design', desc: 'Enterprise-grade security built-in' }
    ],
    technologies: ['Swift', 'Kotlin', 'React Native', 'Flutter', 'TypeScript', 'Firebase']
  },
  web: {
    title: 'Web Development',
    subtitle: 'Modern Web Applications',
    description: 'Create stunning, high-performance web applications that deliver exceptional user experiences and drive conversions.',
    icon: Globe,
    color: 'from-purple-500 to-violet-600',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    services: [
      { name: 'Frontend Development', desc: 'React, Vue, Angular for modern interfaces' },
      { name: 'Backend Development', desc: 'Scalable APIs and server solutions' },
      { name: 'Full Stack Development', desc: 'End-to-end web application development' },
      { name: 'E-Commerce Solutions', desc: 'Online stores that drive sales' },
      { name: 'Website Redesign', desc: 'Modernize your online presence' },
      { name: 'Progressive Web Apps', desc: 'App-like experiences on the web' }
    ],
    features: [
      { icon: TrendingUp, title: 'SEO Optimized', desc: 'Built for search engine visibility' },
      { icon: Zap, title: 'Lightning Fast', desc: 'Optimized for Core Web Vitals' },
      { icon: Users, title: 'User-Centric', desc: 'Designed for conversion' }
    ],
    technologies: ['React', 'Next.js', 'Node.js', 'TypeScript', 'Python', 'GraphQL', 'PostgreSQL']
  },
  database: {
    title: 'Database Services',
    subtitle: 'Data Management Excellence',
    description: 'Optimize your data infrastructure with our expert database services. From design to migration, we ensure your data works for you.',
    icon: Server,
    color: 'from-orange-500 to-red-600',
    image: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=800&q=80',
    services: [
      { name: 'Database Architecture Design', desc: 'Scalable data structures for your needs' },
      { name: 'Database Migration', desc: 'Seamless data transfer between systems' },
      { name: 'Database Administration', desc: 'Expert DBA services and management' },
      { name: 'Performance Optimization', desc: 'Speed up your database queries' },
      { name: 'Database Security', desc: 'Protect your valuable data assets' },
      { name: 'Managed Services', desc: '24/7 database monitoring and support' }
    ],
    features: [
      { icon: Shield, title: 'Data Security', desc: 'Enterprise-grade protection for your data' },
      { icon: TrendingUp, title: 'High Availability', desc: '99.9% uptime guaranteed' },
      { icon: Clock, title: 'Fast Recovery', desc: 'Robust backup and disaster recovery' }
    ],
    technologies: ['SQL Server', 'PostgreSQL', 'MongoDB', 'MySQL', 'Oracle', 'Redis', 'Elasticsearch']
  },
  dotnet: {
    title: '.NET Services',
    subtitle: 'Microsoft Technology Stack',
    description: 'Leverage the power of Microsoft .NET for robust, scalable enterprise applications that integrate seamlessly with your existing systems.',
    icon: Code,
    color: 'from-cyan-500 to-blue-600',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
    services: [
      { name: '.NET Core Development', desc: 'Cross-platform applications with .NET Core' },
      { name: 'ASP.NET Web Applications', desc: 'Robust web solutions with ASP.NET' },
      { name: 'Entity Framework', desc: 'Efficient data access with EF Core' },
      { name: 'Web API Development', desc: 'RESTful APIs for modern applications' },
      { name: 'Blazor Applications', desc: 'Interactive web UIs with C#' },
      { name: 'Legacy Migration', desc: 'Modernize your .NET Framework apps' }
    ],
    features: [
      { icon: Layers, title: 'Enterprise Ready', desc: 'Built for large-scale deployments' },
      { icon: Settings, title: 'Highly Configurable', desc: 'Flexible architecture patterns' },
      { icon: Shield, title: 'Secure', desc: 'Microsoft-backed security features' }
    ],
    technologies: ['.NET 8', 'C#', 'ASP.NET Core', 'Blazor', 'Entity Framework', 'Azure DevOps', 'SignalR']
  },
  azure: {
    title: 'Azure Services',
    subtitle: 'Cloud-First Solutions',
    description: 'Accelerate your cloud journey with our Azure expertise. From migration to optimization, we help you maximize your cloud investment.',
    icon: Cloud,
    color: 'from-sky-500 to-blue-600',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
    services: [
      { name: 'Azure Migration', desc: 'Seamless move to Azure cloud' },
      { name: 'Azure DevOps', desc: 'CI/CD pipelines and automation' },
      { name: 'Azure AI Services', desc: 'Machine learning and cognitive services' },
      { name: 'Azure App Services', desc: 'Scalable web and API hosting' },
      { name: 'Azure Security', desc: 'Comprehensive cloud security solutions' },
      { name: 'Cost Optimization', desc: 'Reduce your Azure spend' }
    ],
    features: [
      { icon: TrendingUp, title: 'Scalable', desc: 'Scale resources on demand' },
      { icon: Shield, title: 'Compliant', desc: 'Meet regulatory requirements' },
      { icon: Zap, title: 'Global Reach', desc: 'Deploy worldwide instantly' }
    ],
    technologies: ['Azure Functions', 'Azure Kubernetes', 'Cosmos DB', 'Azure SQL', 'App Services', 'Logic Apps', 'Azure AD']
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const pageRef = useRef(null);
  const service = serviceData[serviceId] || serviceData.sap;
  const IconComponent = service.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [serviceId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            {/* Breadcrumb */}
            <Link to="/services" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 reveal">
              <ArrowLeft className="w-4 h-4" />
              Back to Services
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="reveal-left">
                <div className={`inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r ${service.color} text-white rounded-full shadow-lg mb-6`}>
                  <IconComponent className="w-5 h-5" />
                  <span className="text-sm font-medium">{service.subtitle}</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  {service.title}
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {service.description}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <a
                    href="#services-list"
                    className="inline-flex items-center gap-2 px-6 py-4 text-gray-700 font-semibold hover:text-[#ff6b35] transition-colors"
                  >
                    View Services
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              <div className="reveal-right relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-20`}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services List */}
        <section id="services-list" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-orange-50 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                What We Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Our {service.title}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.services.map((item, index) => (
                <div 
                  key={item.name}
                  className="reveal-scale bento-card group cursor-pointer hover:border-[#ff6b35]/30"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Our Advantages
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {service.features.map((feature, index) => (
                <div 
                  key={feature.title}
                  className="reveal-scale text-center"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12 reveal">
              <span className="inline-block px-4 py-2 bg-white/10 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Technologies
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Tools & Technologies
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {service.technologies.map((tech, i) => (
                <div 
                  key={tech}
                  className="reveal-scale px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full text-white font-medium border border-white/20 hover:bg-white/20 hover:border-[#ff6b35]/50 transition-all cursor-default"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  {tech}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 reveal">
              Ready to Get Started?
            </h2>
            <p className="text-white/90 text-lg mb-10 reveal">
              Let's discuss how our {service.title.toLowerCase()} can transform your business.
            </p>
            <Link 
              to="/contact"
              className="reveal inline-flex items-center gap-3 bg-white text-[#ff6b35] px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
            >
              Contact Us Today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default ServiceDetailPage;
